import { Component, RenderableProps, h } from "preact";
import { Board, BoardPosition } from '../lib/wordboard';
import { generateBoard, lookupFullWords, fullWordset, generatorWordSet } from '../lib/vowelboard';
import { LetterGrid } from './lettergrid';
import { selectionStartsOrExtendsDirection, posEqual, isPositionInWord, numWordsAtPosition } from '../lib/boardhelper';
import { Progress } from './progress';
import { Clue, ClueAnimation } from './clue';
import { DragManagerUnsubscribe, pointerDragManager } from '../lib/touchhelper';
import { trackEvent, trackPageView } from '../lib/analytics';
import { applySelectionToPattern, patternSlotCount, addHintToPattern } from '../lib/patternhelper';
import { AnimationManager } from '../lib/animationmanager';
import { CanHibernate } from '../lib/util';
import { TutorialWordList } from './findpatterngametutorial';



export interface FindPatternGameProps extends CanHibernate<FindPatternGameData> {
    initialGameData: FindPatternGameData,
    isTutorial?: boolean,
    canSkipTutorial?: boolean,
    onShowHelpPressed?: ()=>void,
    onBackPressed: ()=>void,
    saveGame?: (state:FindPatternGameData)=>void,
    onSkipTutorial?: ()=>void,
    onComplete: (gameData: FindPatternGameData)=>void
}


export interface FindPatternGameData {
    removeLetters: boolean,
    board: Board,
    patternList: string[],
    currentPatternIdx: number,
    foundWords: number[],
}


export interface FindPatternGameState {
    removeLetters: boolean,
    board: Board,
    patternList: string[],
    currentPatternIdx: number,
    selectedLetters: BoardPosition[],
    clueAnimation: ClueAnimation,
    completeAnimationClass: string,
    foundWords: number[],
    levelComplete: boolean,
    scoreDisplay: number,
    expectedWordDisplay: string
}

export class GameActions {
    static foundWord(state: FindPatternGameState) {
        var { currentPatternIdx, foundWords, patternList } = state;
        var newFoundWords = [...foundWords, currentPatternIdx];

        const newState = {
            clueAnimation: ClueAnimation.CorrectWord,
            foundWords: newFoundWords,
            currentPatternIdx: newFoundWords.length < patternList.length ?  GameActions.nextClue(state, 1) : state.currentPatternIdx
        } 

        return newState;
    }

    static wrongWord(state: FindPatternGameState) {
        return {
            clueAnimation: ClueAnimation.WrongWord,
        }
    }

    static nextClue(state: FindPatternGameState, delta: number) {
        var { foundWords, patternList, currentPatternIdx } = state;
        var unsolvedPatterns = patternList.map((_, idx) => idx).filter(idx => foundWords.indexOf(idx) < 0)
        var unsolvedIdx = unsolvedPatterns.indexOf(currentPatternIdx);
        unsolvedIdx = unsolvedIdx + delta;
        if (unsolvedIdx >= unsolvedPatterns.length) {
            unsolvedIdx = 0;
        }
        if (unsolvedIdx < 0) {
            unsolvedIdx = unsolvedPatterns.length - 1;
        }
        currentPatternIdx = unsolvedPatterns[unsolvedIdx];
        return currentPatternIdx;
    }

    static GameStateFromData(gameData: FindPatternGameData): FindPatternGameState {
        return {
            ...gameData,
            selectedLetters: [],
            clueAnimation: ClueAnimation.None,
            completeAnimationClass: '',
            scoreDisplay: gameData.foundWords.length,
            levelComplete: gameData.foundWords.length == gameData.patternList.length,
            expectedWordDisplay: null
        }
    }


    static newLevel(size: number): FindPatternGameState {
        let gameData = createGame(size);
        return this.GameStateFromData(gameData)
    }

    static selectLetter({ selectedLetters }: FindPatternGameState, position: BoardPosition) {
        return {
            selectedLetters: [...selectedLetters, position]
        }
    }

    static replaceLastSelectedLetter({ selectedLetters }: FindPatternGameState, position: BoardPosition) {
        let l = selectedLetters.slice(0, selectedLetters.length - 1)
        return {
            selectedLetters: [...l, position]
        }
    }

    static cancelSelection(_: FindPatternGameState){
        return {
            selectedLetters: [] as BoardPosition[]
        }
    }
}


function randomElement<T>(a: Array<T>): T {
    return a[Math.trunc(Math.random() * a.length)]
}


function makePattern(word:string): string {
    return word.replace(/[^aeiou]/g, ' ')
}

function uniqueWordForConsonants(consonants:string, patternlist: string[]) {
    let full = lookupFullWords(consonants)
    let uniqueFull = full.filter(w => patternlist.indexOf(makePattern(w)) < 0);
    if (uniqueFull.length < 1) {
        return null;
    }
    return randomElement(uniqueFull);
}


export function createGame(size: number): FindPatternGameData {
    var iter = 0;
    for (var j=0; j < 20; j++) {
        var board = generateBoard(size, size);

        //shuffle a little
        board.Words.sort(function (a, b) { return 0.5 - Math.random() });

        var consonantWords = board.Words.map(w => w.Word);
        var wordlist:string[] = []
        var patternlist:string[] = []
        var dupelist:number[] = [];
        consonantWords.forEach((c,idx) => {
            let word = uniqueWordForConsonants(c, patternlist);
            if (!word) {
                dupelist.push(idx);
                wordlist.push("");
                patternlist.push("");
            } else {
                wordlist.push(word);
                patternlist.push(makePattern(word))
            }
        });


        try {
            //try a little shuffle
            for (var dupeIdx of dupelist.slice()) {
                var dupeWord = consonantWords[dupeIdx]
                var full = lookupFullWords(dupeWord);
                var collisions = full.map(w => patternlist.indexOf(makePattern(w)));
                var okword = collisions.indexOf(-1);
                if (okword >= 0) {
                    dupelist.splice(dupelist.indexOf(dupeIdx),1)
                    wordlist[dupeIdx] = full[okword]
                    patternlist[dupeIdx] = makePattern(full[okword])
                    continue;
                }

                //ask one of the dupes to change
                var didFix = false;
                for (var candIdx of collisions) {
                    var candWord = consonantWords[candIdx];
                    //patternlist already contains the pattern candWord is using so the following will pick a new unused pattern if available
                    var newWord = uniqueWordForConsonants(candWord, patternlist);
                    if (newWord) {
                        wordlist[candIdx] = newWord;
                        patternlist[candIdx] = makePattern(newWord);

                        var newWordForDupe = uniqueWordForConsonants(dupeWord, patternlist);
                    
                        wordlist[dupeIdx] = newWordForDupe;
                        patternlist[dupeIdx] = makePattern(newWordForDupe);
                        dupelist.splice(dupelist.indexOf(dupeIdx),1);
                        didFix = true;
                        break;
                    }
                }
            }
        } catch {
            continue;
        }

        if (dupelist.length > 0) {
            //we delete the duplicate words and ensure each letter has one word covering it
            
            board.Words = board.Words.filter((w,i)=> dupelist.indexOf(i) < 0);
            wordlist = wordlist.filter((w,i) => dupelist.indexOf(i) < 0);
            patternlist = patternlist.filter((w,i) => dupelist.indexOf(i) < 0);
            try {
                for (var c = 0; c < board.Cols; c++) {
                    for (var r = 0; r < board.Rows; r++) {
                        if (numWordsAtPosition({ Row: r, Col: c}, board.Words) < 1) {
                            throw "word removal left a hole";
                        }
                    }
                }
            } catch {
                continue;
            }
        }

        iter = j;

        if (patternlist.length == new Set(patternlist).size) break;
    }

    console.log("found after ",j);
    console.log("wordlist: ", wordlist);
    console.log("patternList", patternlist);
    return {
        removeLetters: true,
        board: board,
        patternList: patternlist,
        currentPatternIdx: 0,
        foundWords: [],
    }
}


const animationClasses = ["puff", "swirl", "slide-out-blurred", "swing-out", "roll-out", "slide-out-elliptic"];

export class FindPatternGame extends Component<FindPatternGameProps, FindPatternGameState> {

    dragSubscriptions: DragManagerUnsubscribe[] = [];

    anim: AnimationManager = new AnimationManager();
    hasLearnedSkipForward: boolean;
    hasLearnedSkipBackward: boolean;

    constructor(props: FindPatternGameProps) {
        super(props);
        if (this.props.onHibernate)
            this.props.onHibernate(() => { return this.state })
    
        this.state = GameActions.GameStateFromData(props.hibernateState || props.initialGameData)
        if (!this.props.isTutorial && this.props.saveGame) {
            this.props.saveGame(this.state);
        }

        this.hasLearnedSkipForward = false;
        this.hasLearnedSkipBackward = false;
    }


    currentPattern() {
        return this.state.patternList[this.state.currentPatternIdx];
    }

    trySelectLetter = (row: number, col: number) => {
        //only select letters when we aren't doing anything
        if (this.state.clueAnimation != ClueAnimation.None) return;

        var pos = { Row: row, Col: col };
        // TODO fix this horrible logic
        var { selectedLetters } = this.state;
        if (selectionStartsOrExtendsDirection(selectedLetters, pos)) {
            let letter = this.state.board.Letters[row][col];
            let proposedWord = applySelectionToPattern(this.selectedConsonants()+letter, this.currentPattern());

            if (proposedWord != null && selectedLetters.length < patternSlotCount(this.currentPattern())) {
                this.setState(GameActions.selectLetter(this.state, pos))
            }
        } else {
            if (selectedLetters.length == 2) {
                let changedSelection = selectedLetters.slice(0, -1)
                //try change our last letter to change direction
                if (selectionStartsOrExtendsDirection(changedSelection, pos)) {

                    let letter = this.state.board.Letters[row][col];
                    let selected = this.positionsToWord(changedSelection);
                    let proposedWord = applySelectionToPattern(selected+letter, this.state.patternList[this.state.currentPatternIdx]);

                    if (proposedWord != null) {
                        this.setState(GameActions.replaceLastSelectedLetter(this.state, pos))
                    }
                }
            }
        }
    }

    componentDidUpdate(prevProps: FindPatternGameProps, prevState: FindPatternGameState) {
        if (prevState != this.state && !this.props.isTutorial && this.props.saveGame) {
            this.props.saveGame(this.state)
        }
    }

    positionsToWord = (positions: BoardPosition[]): string => positions.map(p => this.state.board.Letters[p.Row][p.Col]).join("")

    componentDidMount() {
        this.dragSubscriptions.push(pointerDragManager.onDragStop(this.handleSelectionFinish));
    }

    componentWillUnmount() {
        this.dragSubscriptions.forEach(unsub => unsub());
        this.dragSubscriptions = [];
        this.anim.endanimation();
    }

    selectedConsonants = () => {
        return this.state.selectedLetters.map(p => this.state.board.Letters[p.Row][p.Col]).join("");
    }

    selectedWord = () => {
        return applySelectionToPattern(this.selectedConsonants(), this.currentPattern())
    }

    handleSelectionFinish = () => {
        let { selectedLetters, levelComplete, clueAnimation, patternList, currentPatternIdx, board } = this.state;
        if (levelComplete || (selectedLetters.length == 0) || clueAnimation != ClueAnimation.None) {
            return true; //we aren't interested
        }
        
        var selectedWord = this.selectedWord()

        //we haven't finished a full selection, just cance.
        if (selectedLetters.length < patternSlotCount(this.currentPattern())) {
            this.setState(GameActions.cancelSelection(this.state))
            return false;
        }

        let expectedWord = board.Words[currentPatternIdx];
        let expectedFullWord = applySelectionToPattern(expectedWord.Word, this.currentPattern())
        let wasExpected = posEqual(expectedWord.Position, selectedLetters[0]) && isPositionInWord(selectedLetters[selectedLetters.length - 1], expectedWord)
                        || posEqual(expectedWord.Position, selectedLetters[selectedLetters.length - 1]) && isPositionInWord(selectedLetters[0], expectedWord)
                        || expectedWord.Word == this.selectedConsonants() //sometimes there are dupes, just give it to them for now //TODO prevent this

        if (fullWordset.has(selectedWord)) {
            this.anim.endanimation();

            if (wasExpected) {
                
                this.setState(GameActions.foundWord(this.state));
                trackEvent("foundword", this.state.foundWords.length)

                this.anim.after(1500,
                    () => {
                        let levelcomplete = this.state.foundWords.length == this.state.patternList.length;
                        this.setState({
                            selectedLetters: [],
                            levelComplete: levelcomplete,
                            completeAnimationClass: levelcomplete ? randomElement(animationClasses) : "",
                            clueAnimation: ClueAnimation.None,
                            scoreDisplay: this.state.foundWords.length
                        });

                        if (levelcomplete) {
                            trackEvent("levelcomplete", this.state.board.Cols)
                            this.anim.after(4500, () => {
                                //this.setState({
                                    //completeAnimationClass: ""
                                //});
                                this.props.onComplete(this.state);
                            })
                        }
                    }
                );

            } else {
                //found a bonus word
                trackEvent("foundBonusWord")
                console.log("found bonus word but was expecting", expectedWord)
               
                //add a letter to the pattern list;
                const newPatternList = patternList.slice();
                let currentPat =  newPatternList[currentPatternIdx];

                let newPattern = addHintToPattern(currentPat, expectedFullWord, selectedWord);
                newPatternList[currentPatternIdx] = newPattern;

                this.setState({
                    clueAnimation: ClueAnimation.BonusWord,
                    expectedWordDisplay: "blah",
                    patternList: newPatternList
                })

                this.anim.after(1500, () =>
                    this.setState({
                        selectedLetters: [],
                        clueAnimation: ClueAnimation.None
                    })
                );
            }

        } else {
            this.anim.endanimation();
            this.setState(GameActions.wrongWord(this.state))
            this.anim.after(1500, () =>
                this.setState({
                    selectedLetters: [],
                    clueAnimation: ClueAnimation.None
                })
            );
        }
        return false;
    }


    onNewLevel = (size: number) => {
        trackEvent("playagain");
        trackPageView('/game/' + size);
        this.setState(GameActions.newLevel(size))
    }

    onBack = () => {
        console.log("got click");
        this.props.onBackPressed && this.props.onBackPressed();
    }

    onShowHelp = () => {
        console.log("got Click", this.props)
        this.props.onShowHelpPressed && this.props.onShowHelpPressed()
    }

    changeClue = (delta: number) => {
        //they will get a new clue in a minute.
        if (this.state.clueAnimation == ClueAnimation.CorrectWord) return;
        this.anim.endanimation();
        trackEvent("change_clue")
        this.setState({
            clueAnimation: delta > 0 ? ClueAnimation.NextWord : ClueAnimation.PrevWord,
            selectedLetters: [],
            currentPatternIdx: GameActions.nextClue(this.state, delta),
        });
        this.anim.after(200, () => this.setState({ clueAnimation: ClueAnimation.None }))
    }

    changeClueTutorial = (delta: number) => {
        if (delta == 1 && !this.hasLearnedSkipForward) {
            this.hasLearnedSkipForward = true;
            this.changeClue(1)
        } 
        
        if (delta == -1 && this.hasLearnedSkipForward && !this.hasLearnedSkipBackward) {
            this.hasLearnedSkipBackward = true;
            this.changeClue(-1);
        }
    }

    renderTutorial() {
            var { board, selectedLetters, completeAnimationClass, foundWords, patternList, currentPatternIdx, levelComplete, clueAnimation, scoreDisplay } = this.state;
            
            let onSkip = () => this.props.onSkipTutorial && this.props.onSkipTutorial()
            let onBack = () => this.props.onBackPressed &&  this.props.onBackPressed()
           
            var remainingWords = patternList.length - foundWords.length;
    
            let learningSkipForward = (currentPatternIdx == 2 && !this.hasLearnedSkipForward)
            let learningSkipBackward = (currentPatternIdx == 3 && !this.hasLearnedSkipBackward)
    
            var clueSectionClass = ""
           
            if (clueAnimation != ClueAnimation.CorrectWord) {
                if (learningSkipForward) 
                    clueSectionClass = "tutorial-skip-forward"
                if (learningSkipBackward) 
                    clueSectionClass = "tutorial-skip-backward"
            }
    
            let skipHandler = (learningSkipForward || learningSkipBackward) ? this.changeClueTutorial : null;
            
            var clueSection = (<div class={"clue-section "+ clueSectionClass }  style={ levelComplete ? { visibility: "hidden"} : {} } >
                 <Clue onChangeClue={skipHandler}  word={patternList[currentPatternIdx]} selectedLetters={this.positionsToWord(selectedLetters).split("")} currentAnimation={clueAnimation} ></Clue>
             </div>)
    
    
            let selectLetterHandler = (learningSkipForward || learningSkipBackward) ? ()=>{} : this.trySelectLetter
    
            var countWords = foundWords; //(clueAnimation == ClueAnimation.CorrectWord) ? foundWords.slice(0,-1) : foundWords;
            var counts = board.Letters.map( 
                (col,rowNum) => col.map(
                    (_, colNum) => numWordsAtPosition(
                        {Row: rowNum, Col: colNum}, 
                        board.Words.filter((w,idx)=> countWords.indexOf(idx) < 0 )
                    )
                )
            )
    
            var grid = <LetterGrid letters={board.Letters} counts={counts} onTrySelectLetter={selectLetterHandler} selectedLetters={selectedLetters}></LetterGrid>
    
            if (levelComplete && completeAnimationClass == "") {
                grid = (
                    <div class="grid-complete">
                        <h1>You've got it!</h1>
                        {   this.props.canSkipTutorial
                             ? <a onClick={onSkip}>Let's play</a>
                             : <a onClick={onBack}>Continue</a> 
                        }
                    </div>
                )
                clueSection = <div class="clue-section" style={ { padding: "0"} }></div>
            }
    
           
            var tutorialWordClass = TutorialWordList[currentPatternIdx];
            if (levelComplete) {
                tutorialWordClass = 'done';
            }
            if (learningSkipForward || learningSkipBackward) {
                tutorialWordClass = 'clue';
            }
            if (clueAnimation == ClueAnimation.CorrectWord) {
                tutorialWordClass = 'correct';
            }
            
           
            var tutorialPattern = currentPatternIdx;
            let clueIsProceeding = (clueAnimation == ClueAnimation.CorrectWord ||  clueAnimation == ClueAnimation.NextWord)
    
            var clueIsAnimating = (clueIsProceeding || clueAnimation == ClueAnimation.PrevWord)
            if (clueIsProceeding && tutorialPattern > 0) {
                tutorialPattern = tutorialPattern - 1;
            }
            if (clueAnimation == ClueAnimation.PrevWord) {
                tutorialPattern = tutorialPattern + 1;
            }
    
            var tutorialSection = 
                <span>
                    Solve the clue by swiping the consonants in the grid.
                </span>
    
            if (tutorialPattern == 1) {
                tutorialSection = 
                <span>
                    Words can be forward or reverse.
                </span>
            }
    
            if (tutorialPattern == 2 && (learningSkipForward || learningSkipBackward) ) {
                tutorialSection = <span>
                    Change the clue by using the arrows. 
                </span>
            }
    
            if (tutorialPattern == 3 && (learningSkipBackward || (clueAnimation == ClueAnimation.PrevWord))) {
                tutorialSection = <span>
                    Revisit the clue later with fresh inspiriation.
                </span>
            }
            
    
            if (tutorialPattern == 2 && this.hasLearnedSkipBackward && clueAnimation != ClueAnimation.PrevWord) {
                tutorialSection = <span>
                    Letters must be swiped in a straight line.
                </span>
            }
    
            if (tutorialPattern == 3 && this.hasLearnedSkipBackward && clueAnimation != ClueAnimation.PrevWord) {
                tutorialSection = <span>
                    Letters may dim when they are in no more words.
                </span>
            }
    
            if (tutorialPattern == 4 || foundWords.length == TutorialWordList.length) {
                tutorialSection = <span>
                    Words can also be found diagonally.
                </span>
            }
            
            var tutorialSectionClass = "current"
            if (clueIsAnimating || levelComplete)
                if ( !(tutorialPattern == 2 && this.hasLearnedSkipBackward) )
                    tutorialSectionClass = "next"
    
            if (levelComplete && !clueIsAnimating) {
                tutorialSectionClass = "complete"
            }
    
            let helpButton = this.props.canSkipTutorial ? <a class="skip-button" onClick={onSkip}>Skip <span class="skip-button-arrow">&#8250;</span></a> : <a class="skip-button">&nbsp;</a>
            let backButton = !this.props.canSkipTutorial ? <a class="back-button" onClick={onBack} >&#8249;</a> : <a class="back-button">&nbsp;</a>
    
            return (
            <div class="game-screen tutorial">
                <div class="title-section">
                    {backButton}
                    <span>Tutorial</span>
                    {helpButton}
                </div>
                <div class="content-section">
                <div class="game-container" >
                    
                    <div class={"progress-section"+ (levelComplete ? " complete": "")} >
                        <Progress done={scoreDisplay} total={patternList.length}></Progress>
                    </div>
                    <div class={"tutorial-section " + tutorialSectionClass}>
                        {tutorialSection}
                    </div>
                      {clueSection}
                    <div class={"tutorial-word-"+   tutorialWordClass + " grid-section " +completeAnimationClass +  ( completeAnimationClass ? " complete " : "")}>
                        {grid}
                    </div>
                </div>
                </div>
            </div>
            )
        }
    



    render(props?: RenderableProps<FindPatternGameProps>, state?: FindPatternGameState) {
       
        if (props.isTutorial)
            return this.renderTutorial()
              
        var { board, selectedLetters, completeAnimationClass, foundWords, patternList, currentPatternIdx, levelComplete, clueAnimation, scoreDisplay, removeLetters } = state;

        var countWords = foundWords; //(clueAnimation == ClueAnimation.CorrectWord) ? foundWords.slice(0,-1) : foundWords;
        var counts = removeLetters ? 
            board.Letters.map( 
                (col,rowNum) => col.map(
                    (_, colNum) => numWordsAtPosition(
                        {Row: rowNum, Col: colNum}, 
                        board.Words.filter((w,idx)=> countWords.indexOf(idx) < 0 )
                    )
                )
            )    
            : null;

        var grid = <LetterGrid counts={counts} letters={board.Letters} onTrySelectLetter={this.trySelectLetter} selectedLetters={selectedLetters}></LetterGrid>

        var remainingWords = patternList.length - foundWords.length;

        var expectedClass = (clueAnimation == ClueAnimation.BonusWord) ? "active" : "";

        var clueSection = (<div class="clue-section" style={levelComplete ? { visibility: "hidden" } : {}} >
            <Clue onChangeClue={remainingWords > 1 ? this.changeClue : null} word={patternList[currentPatternIdx]} selectedLetters={this.positionsToWord(selectedLetters).split("")} currentAnimation={clueAnimation} ></Clue>
            <div class={"expected-word " + expectedClass}>Nice Word! There is another..</div>
        </div>)

        if (levelComplete && completeAnimationClass == "") {

            grid = (
                <div class="grid-complete">
                    <h1>Success!</h1>
                    <a onClick={() => this.onNewLevel(this.state.board.Cols)}>Play Again?</a>
                    <a onClick={() => this.onBack()}>Menu</a>
                </div>
            )

            clueSection = <div class="clue-section" style={{ padding: "0" }}></div>
        }

        return (
            <div class="game-screen">
                <div class="title-section">
                    <a class="back-button" onClick={this.onBack}>&#8249;</a>
                    <span>vowels</span>
                    <a class="help-button" onClick={this.onShowHelp}>?</a>
                </div>
                <div class="content-section">
                    <div class="game-container" >
                        <div class={"progress-section" + (levelComplete ? " complete" : "")} >
                            <Progress done={scoreDisplay} total={patternList.length}></Progress>
                        </div>
                        {clueSection}
                        <div class={"grid-section " + completeAnimationClass + ( completeAnimationClass ? " complete " : "")}>
                            {grid}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

}




